/**
 * GZ#3436 — Private Match is not its own booking_type.
 *
 * Stored shape:
 *   booking_type "Open Match" + is_private_match true  → Private Match UI
 *   booking_type "Open Match" + is_private_match false → public Open Match
 *
 * BookingModel used to hydrate `selectedOpenRegular` from booking_type alone,
 * so every existing Private Match opened (and any later `events` refresh
 * after a Players-tab guest add) as "Open Match". Save then sent
 *   open_match_options.private_match: false
 * unless getDetailsById had already corrected the tab (and only when the
 * court sport was is_open_match).
 */

export const isTruthyPrivateMatchFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "true";

/** Calendar row, detail GET, or nested service — any of these may carry the flag. */
export const readIsPrivateMatch = (source: any): boolean => {
  if (!source || typeof source !== "object") return false;
  return (
    isTruthyPrivateMatchFlag(source.is_private_match) ||
    isTruthyPrivateMatchFlag(source.openMatchOptions?.private_match) ||
    isTruthyPrivateMatchFlag(source.service?.is_private_match) ||
    isTruthyPrivateMatchFlag(source.service?.openMatchOptions?.private_match)
  );
};

const storedOpenRegularType = ({
  eventType,
  bookingType,
  lessonAvailableAsBookable,
}: {
  eventType?: string | null;
  bookingType?: string | null;
  lessonAvailableAsBookable?: boolean | null;
}): string =>
  eventType ||
  bookingType ||
  (lessonAvailableAsBookable == true
    ? "Bookable"
    : lessonAvailableAsBookable == false || lessonAvailableAsBookable == null
      ? "Group"
      : "Bookable");

/**
 * UI tab for the booking modal.
 * `showPrivateMatchTab` is the open-match-only sport pair
 * (Private Match | Open Match). When false the sport uses Regular | Open Match
 * and privacy is the checkbox, so the tab stays "Open Match".
 */
export const resolveOpenRegularFromBooking = ({
  eventType,
  bookingType,
  lessonAvailableAsBookable,
  isPrivateMatch,
  showPrivateMatchTab,
}: {
  eventType?: string | null;
  bookingType?: string | null;
  lessonAvailableAsBookable?: boolean | null;
  isPrivateMatch?: unknown;
  showPrivateMatchTab?: boolean | null;
}): string => {
  const stored = storedOpenRegularType({
    eventType,
    bookingType,
    lessonAvailableAsBookable,
  });
  if (
    stored === "Open Match" &&
    isTruthyPrivateMatchFlag(isPrivateMatch) &&
    showPrivateMatchTab !== false
  ) {
    return "Private Match";
  }
  return stored;
};

/** Create / update `open_match_options.private_match`. */
export const privateMatchFlagForPayload = (
  selectedOpenRegular: string,
  privateMatchToggle: boolean,
  opts?: { matchTypeTabs?: boolean }
): boolean => {
  // GZ#4977: on Private Match | Open Match tab sports the leftover
  // `private_match` form value stays true after staff switch to Open Match,
  // so the PUT kept the booking private. The tab is the source of truth.
  if (opts?.matchTypeTabs) {
    return selectedOpenRegular === "Private Match";
  }
  return selectedOpenRegular === "Private Match" ? true : !!privateMatchToggle;
};
